/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonVariety, Checkbox, Heading, Link, Text } from '@sonarsource/echoes-react';
import { noop } from 'lodash';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetValueQuery, useSaveSimpleValueMutation } from '~sq-server-commons/queries/settings';
import { SettingsKey } from '~sq-server-commons/types/settings';

export default function ArchitectureEnablementForm() {
  const { formatMessage } = useIntl();

  const { data: architectureEnabled, isLoading } = useGetValueQuery({
    key: SettingsKey.DesignAndArchitecture,
  });
  const { mutateAsync: saveSettingValue, isPending } = useSaveSimpleValueMutation();
  const [checked, setChecked] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const currentValue = architectureEnabled?.value === 'true';

  const onSetArchitectureEnabled = useCallback(() => {
    saveSettingValue({
      key: SettingsKey.DesignAndArchitecture,
      value: checked.toString(),
    })
      .then(() => {
        setHasChanged(false);
        // It is necessary to reload the page to apply the changes for routes.
        window.location.reload();
      })
      .catch(noop);
  }, [checked, saveSettingValue]);

  const onCancel = useCallback(() => {
    setChecked(currentValue);
    setHasChanged(false);
  }, [currentValue]);

  const isChecked = hasChanged ? checked : currentValue;

  return (
    <div>
      <Heading as="h3" hasMarginBottom>
        {formatMessage({ id: 'settings.design_and_architecture.header' })}
      </Heading>
      <Text as="p" className="sw-my-4">
        <FormattedMessage id="settings.design_and_architecture.description.1" />
      </Text>
      <Text as="p" className="sw-my-4">
        <FormattedMessage id="settings.design_and_architecture.description.2" />
      </Text>
      <Text as="p" className="sw-my-4">
        <FormattedMessage
          id="settings.design_and_architecture.description.3"
          values={{
            link: (text) => (
              <Link
                className="sw-ml-1"
                enableOpenInNewTab
                to="https://www.sonarsource.com/legal/early-access/"
              >
                {text}
              </Link>
            ),
          }}
        />
      </Text>

      <Checkbox
        checked={isChecked}
        className="sw-my-4"
        isDisabled={isLoading ?? isPending}
        label={formatMessage({
          id: 'settings.design_and_architecture.enable',
        })}
        onCheck={() => {
          setChecked(!isChecked);
          setHasChanged(true);
        }}
      />

      {hasChanged && (
        <div className="sw-flex">
          <Button
            isDisabled={isLoading || isPending || !hasChanged || checked === currentValue}
            isLoading={isLoading ?? isPending}
            onClick={onSetArchitectureEnabled}
            variety={ButtonVariety.Primary}
          >
            {formatMessage({ id: 'save' })}
          </Button>
          <Button className="sw-ml-3" onClick={onCancel} variety={ButtonVariety.Default}>
            <FormattedMessage id="cancel" />
          </Button>
        </div>
      )}
    </div>
  );
}
